import { projectService } from '@/services/project/project-service';
import { projectKeys } from '@/utils/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';

export const useDefaultReviewerSelectorData = (
  projectId: MaybeRefOrGetter<string>,
) => {
  const queryClient = useQueryClient();

  const projectUsersQuery = useQuery({
    queryKey: computed(() => projectKeys.reviewers({ projectId: toValue(projectId) })),
    queryFn: async () =>
      await projectService.getProjectReviewers(toValue(projectId)),
    enabled: computed(() => !!toValue(projectId)),
  });

  const defaultReviewersQuery = useQuery({
    queryKey: computed(() => projectKeys.defaultReviewers({ projectId: toValue(projectId) })),
    queryFn: () => projectService.getDefaultReviewers(toValue(projectId)),
    enabled: computed(() => !!toValue(projectId)),
  });

  const defaultReviewersMutationKey = computed(() => [
    'projectService',
    'updateDefaultReviewers',
    toValue(projectId),
  ]);
  const defaultReviewersMutation = useMutation({
    mutationKey: defaultReviewersMutationKey,
    mutationFn: (reviewers: { id: string; ranking: number }[]) =>
      projectService.updateReviewers(toValue(projectId), reviewers),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: projectKeys.defaultReviewers({ projectId: toValue(projectId) }),
      }),
  });

  return {
    projectUsers: { query: projectUsersQuery },
    defaultReviewers: {
      query: defaultReviewersQuery,
      mutation: defaultReviewersMutation,
    },
  };
};
